import React, { useState, useEffect, useRef } from "react";
import {
  Container, Row, Col, Card, Badge, Button, Modal,
  Form, Alert, Spinner, Tabs, Tab,
} from "react-bootstrap";
import { fetchPosts, updatePost, deletePost, publishPost, importCsv, bulkSchedule, recompact } from "./DataService";

// La publication automatique est coupée (services.instagram.publication_auto) :
// un post « scheduled » ne part que sur clic « Publier ». Sa date sert de
// rappel — d'où « Prévu » plutôt que « Planifié », qui laisserait croire qu'il
// partira tout seul.
const STATUS_CONFIG = {
  draft:     { label: "Brouillon",  bg: "secondary" },
  scheduled: { label: "Prévu",      bg: "primary"   },
  published: { label: "Publié",     bg: "success"   },
  failed:    { label: "Échec",      bg: "danger"    },
};

export default function InstagramScreen() {
  const [activeTab, setActiveTab]   = useState("draft");
  const [posts, setPosts]           = useState([]);
  const [counts, setCounts]         = useState({});
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [lastPage, setLastPage]     = useState(1);
  const [search, setSearch]         = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [alert, setAlert]           = useState(null);

  // Modal édition
  const [editPost, setEditPost]     = useState(null);
  const [editCaption, setEditCaption] = useState("");
  const [editScheduled, setEditScheduled] = useState("");
  const [editPhoto, setEditPhoto]   = useState(null); // { id, url } choisie parmi les photos de la livraison
  const [saving, setSaving]         = useState(false);

  // Modal planification individuelle
  const [schedulePost, setSchedulePost] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("10:00");
  const [scheduling, setScheduling]     = useState(false);

  // Modal auto-planification
  const [showAutoSchedule, setShowAutoSchedule] = useState(false);
  const [autoStartDate, setAutoStartDate]       = useState("");
  const [autoScheduling, setAutoScheduling]     = useState(false);

  // Recompacter
  const [showRecompact, setShowRecompact]     = useState(false);
  const [recompacting, setRecompacting]       = useState(false);

  // Import CSV
  const fileRef = useRef();
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setSearchDebounced(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { load(); }, [activeTab, searchDebounced, page]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchPosts(activeTab, searchDebounced || null, page);
      setPosts(data.data.data);
      setCounts(data.counts);
      setLastPage(data.data.last_page);
    } catch { setAlert({ type: "danger", msg: "Erreur de chargement." }); }
    finally { setLoading(false); }
  };

  // Édition
  const openEdit = (post) => {
    setEditPost(post);
    setEditCaption(post.caption);
    setEditScheduled(toLocalInput(post.scheduled_at));
    setEditPhoto(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePost(editPost.id, {
        caption: editCaption,
        scheduled_at: editScheduled || null,
        // Photo changée : l'API recompose l'URL à partir de la photo de la livraison.
        ...(editPhoto ? { pod_image_id: editPhoto.id } : {}),
      });
      setEditPost(null);
      load();
    } catch (e) {
      setAlert({ type: "danger", msg: e?.response?.data?.error || "Erreur sauvegarde." });
    } finally { setSaving(false); }
  };

  // Planification individuelle
  const openSchedule = (post) => {
    setSchedulePost(post);
    // Pré-remplir avec la date planifiée existante ou demain
    if (post.scheduled_at) {
      const local = toLocalInput(post.scheduled_at);
      setScheduleDate(local.substring(0, 10));
      setScheduleTime(local.substring(11, 16));
    } else {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setScheduleDate(tomorrow.toISOString().substring(0, 10));
      setScheduleTime("10:00");
    }
  };

  const handleSchedule = async () => {
    if (!scheduleDate || !scheduleTime) return;
    setScheduling(true);
    try {
      await updatePost(schedulePost.id, { scheduled_at: `${scheduleDate}T${scheduleTime}:00` });
      setSchedulePost(null);
      load();
      setAlert({ type: "success", msg: `Rappel enregistré pour le ${scheduleDate} à ${scheduleTime}` });
    } catch (e) {
      setAlert({ type: "danger", msg: e?.response?.data?.error || "Erreur planification." });
    } finally { setScheduling(false); }
  };

  // Auto-planification
  const handleAutoSchedule = async () => {
    if (!autoStartDate) return;
    setAutoScheduling(true);
    try {
      const result = await bulkSchedule(autoStartDate);
      setShowAutoSchedule(false);
      load();
      setAlert({ type: "success", msg: `${result.scheduled} brouillons datés à partir du ${autoStartDate} (rien ne part tout seul)` });
    } catch (e) {
      setAlert({ type: "danger", msg: e?.response?.data?.error || "Erreur lors de l'auto-planification." });
    } finally { setAutoScheduling(false); }
  };

  // Recompacter
  const handleRecompact = async () => {
    setRecompacting(true);
    try {
      const result = await recompact();
      setShowRecompact(false);
      load();
      setAlert({ type: "success", msg: result.message });
    } catch (e) {
      setAlert({ type: "danger", msg: "Erreur lors du recompactage." });
    } finally { setRecompacting(false); }
  };

  // Suppression
  const handleDelete = async (post) => {
    if (!window.confirm("Supprimer ce post ?")) return;
    try {
      await deletePost(post.id);
      load();
    } catch (e) {
      setAlert({ type: "danger", msg: e?.response?.data?.error || "Erreur suppression." });
    }
  };

  // Publication
  const handlePublish = async (post) => {
    if (!window.confirm("Publier maintenant sur Instagram ?")) return;
    try {
      await publishPost(post.id);
      setAlert({ type: "success", msg: "Post publié !" });
      load();
    } catch (e) {
      setAlert({ type: "warning", msg: e?.response?.data?.error || "Erreur publication." });
    }
  };

  // Import CSV
  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importCsv(file);
      setImportResult(result);
      load();
    } catch {
      setAlert({ type: "danger", msg: "Erreur lors de l'import." });
    } finally { setImporting(false); fileRef.current.value = ""; }
  };

  const tabLabel = (key) => `${STATUS_CONFIG[key].label} (${counts[key] ?? 0})`;

  const toLocalInput = (utcStr) => {
    if (!utcStr) return "";
    const d = new Date(utcStr);
    const offset = d.getTimezoneOffset() * 60000;
    return new Date(d - offset).toISOString().substring(0, 16);
  };

  const formatDate = (dt) => dt
    ? new Date(dt).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <Container fluid className="py-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">📸 Posts Instagram</h4>
          <small className="text-muted">{counts.total ?? 0} posts au total</small>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          {activeTab === "draft" && (
            <Button variant="outline-primary" size="sm" onClick={() => setShowAutoSchedule(true)}>
              📅 Répartir les dates
            </Button>
          )}
          {activeTab === "scheduled" && (
            <Button variant="outline-warning" size="sm" onClick={() => setShowRecompact(true)}>
              🔁 Recompacter
            </Button>
          )}
          <Button variant="outline-secondary" size="sm" onClick={() => fileRef.current.click()} disabled={importing}>
            {importing ? <Spinner size="sm" animation="border" /> : "⬆️ Importer CSV"}
          </Button>
          <input ref={fileRef} type="file" accept=".csv" style={{ display: "none" }} onChange={handleImport} />
        </Col>
      </Row>

      <Alert variant="info" className="py-2">
        <strong>Publication automatique désactivée.</strong> Un post ne part sur Instagram que quand tu cliques
        <strong> 🚀 Publier</strong>. Les dates prévues servent de rappel.
      </Alert>

      {alert && <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.msg}</Alert>}
      {importResult && (
        <Alert variant="success" dismissible onClose={() => setImportResult(null)}>
          ✅ {importResult.created} posts importés.
          {importResult.errors > 0 && ` (${importResult.errors} lignes ignorées)`}
        </Alert>
      )}

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="py-2">
          <Form.Control
            placeholder="Rechercher dans les captions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </Card.Body>
      </Card>

      <Tabs activeKey={activeTab} onSelect={k => { setActiveTab(k); setPage(1); }} className="mb-3">
        {Object.keys(STATUS_CONFIG).map(key => (
          <Tab key={key} eventKey={key} title={tabLabel(key)} />
        ))}
      </Tabs>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : posts.length === 0 ? (
        <p className="text-muted text-center py-4">Aucun post dans cette catégorie.</p>
      ) : (
        <Row className="g-3">
          {posts.map(post => (
            <Col key={post.id} xs={12} md={6} lg={4}>
              <Card className="h-100 border-0 shadow-sm">
                <div style={{ position: "relative" }}>
                  <img
                    src={post.photo_url}
                    alt="post"
                    style={{ width: "100%", height: 220, objectFit: "cover", borderRadius: "8px 8px 0 0" }}
                    onError={e => { e.target.src = "https://placehold.co/400x220?text=Photo"; }}
                  />
                  <Badge
                    bg={STATUS_CONFIG[post.status]?.bg}
                    style={{ position: "absolute", top: 8, right: 8, fontSize: 11 }}
                  >
                    {STATUS_CONFIG[post.status]?.label}
                  </Badge>
                </div>
                <Card.Body className="d-flex flex-column">
                  <p className="small text-muted mb-2" style={{ whiteSpace: "pre-wrap", maxHeight: 80, overflow: "hidden" }}>
                    {post.caption}
                  </p>
                  {post.scheduled_at && post.status !== "published" && (
                    <p className="small text-primary mb-2">
                      📅 Prévu le {formatDate(post.scheduled_at)}
                      {/* Plus rien ne part tout seul : une date passée = un post à publier. */}
                      {new Date(post.scheduled_at) <= new Date() && (
                        <Badge bg="warning" text="dark" className="ms-2">à publier</Badge>
                      )}
                    </p>
                  )}
                  {post.published_at && (
                    <p className="small text-success mb-2">✅ Publié le {formatDate(post.published_at)}</p>
                  )}
                  {post.error_message && (
                    <p className="small text-danger mb-2">⚠️ {post.error_message}</p>
                  )}
                  {post.status !== "published" && (
                    <div className="d-flex gap-1 mt-auto flex-wrap">
                      <Button variant="outline-primary" size="sm" onClick={() => openEdit(post)}>✏️</Button>
                      <Button variant="outline-secondary" size="sm" onClick={() => openSchedule(post)}>📅</Button>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(post)}>🗑️</Button>
                      <Button variant="success" size="sm" className="ms-auto" onClick={() => handlePublish(post)}>
                        🚀 Publier
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {lastPage > 1 && (
        <div className="d-flex justify-content-center gap-2 mt-4">
          <Button variant="outline-secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>←</Button>
          <span className="small align-self-center text-muted">Page {page} / {lastPage}</span>
          <Button variant="outline-secondary" size="sm" disabled={page === lastPage} onClick={() => setPage(p => p + 1)}>→</Button>
        </div>
      )}

      {/* Modal édition */}
      <Modal show={!!editPost} onHide={() => setEditPost(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Éditer le post #{editPost?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editPost && (
            <Row>
              <Col md={5}>
                <img src={editPhoto?.url || editPost.photo_url} alt="post"
                  style={{ width: "100%", borderRadius: 8, maxHeight: 300, objectFit: "cover" }} />
                {/* Plusieurs photos sur la livraison : choisir celle du post. */}
                {(editPost.photos_disponibles || []).length > 1 && (
                  <>
                    <div className="small text-muted mt-2 mb-1">
                      {editPost.photos_disponibles.length} photos sur cette livraison — clique pour choisir :
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {editPost.photos_disponibles.map((photo) => {
                        const choisie = (editPhoto?.url || editPost.photo_url) === photo.url;
                        return (
                          <img
                            key={photo.id}
                            src={photo.url}
                            alt={`photo ${photo.id}`}
                            onClick={() => setEditPhoto(photo)}
                            title={choisie ? "Photo du post" : "Choisir cette photo"}
                            style={{
                              width: 64, height: 64, objectFit: "cover", borderRadius: 6, cursor: "pointer",
                              border: choisie ? "3px solid #0d6efd" : "3px solid transparent",
                              opacity: choisie ? 1 : 0.75,
                            }}
                          />
                        );
                      })}
                    </div>
                  </>
                )}
              </Col>
              <Col md={7}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">Caption</Form.Label>
                  <Form.Control as="textarea" rows={8} value={editCaption}
                    onChange={e => setEditCaption(e.target.value)} />
                  <Form.Text className="text-muted">{editCaption.length} caractères</Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditPost(null)}>Annuler</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Sauvegarde..." : "Enregistrer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal planification individuelle */}
      <Modal show={!!schedulePost} onHide={() => setSchedulePost(null)}>
        <Modal.Header closeButton>
          <Modal.Title>📅 Prévoir une date pour le post #{schedulePost?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col xs={7}>
              <Form.Group>
                <Form.Label className="fw-semibold">Date</Form.Label>
                <Form.Control type="date" value={scheduleDate}
                  onChange={e => setScheduleDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={5}>
              <Form.Group>
                <Form.Label className="fw-semibold">Heure</Form.Label>
                <Form.Control type="time" value={scheduleTime}
                  onChange={e => setScheduleTime(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>
          {scheduleDate && scheduleTime && (
            <p className="text-muted small mt-3 mb-0">
              Rappel le <strong>{new Date(`${scheduleDate}T${scheduleTime}`).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })} à {scheduleTime}</strong>.
              Le post ne partira pas tout seul : publie-le avec 🚀 Publier.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => { updatePost(schedulePost.id, { scheduled_at: null }); setSchedulePost(null); load(); }}>
            Retirer la date
          </Button>
          <Button variant="primary" onClick={handleSchedule} disabled={scheduling || !scheduleDate}>
            {scheduling ? "Enregistrement..." : "Prévoir"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal auto-planification */}
      <Modal show={showAutoSchedule} onHide={() => setShowAutoSchedule(false)}>
        <Modal.Header closeButton>
          <Modal.Title>📅 Répartir les dates des brouillons</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Donne une date de rappel aux <strong>{counts.draft ?? 0} brouillons sans date</strong>, à raison de 2 posts/jour (9h30 et 14h00) en semaine,
            1 seul le samedi matin (9h30). Dimanches et jours fériés/fermetures exclus. Rien n'est publié automatiquement.
          </p>
          <Form.Group>
            <Form.Label className="fw-semibold">Commencer le</Form.Label>
            <Form.Control type="date" value={autoStartDate}
              min={new Date().toISOString().substring(0, 10)}
              onChange={e => setAutoStartDate(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAutoSchedule(false)}>Annuler</Button>
          <Button variant="primary" onClick={handleAutoSchedule} disabled={autoScheduling || !autoStartDate}>
            {autoScheduling ? <><Spinner size="sm" animation="border" className="me-2" />Répartition...</> : "Répartir les dates"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal recompacter */}
      <Modal show={showRecompact} onHide={() => setShowRecompact(false)}>
        <Modal.Header closeButton>
          <Modal.Title>🔁 Recompacter les dates prévues</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small">
            Redistribue les dates de rappel des <strong>{counts.scheduled ?? 0} posts prévus</strong> à raison de 2 posts/jour (9h30 et 14h00) en semaine,
            1 seul le samedi matin (9h30), sans trou à partir de maintenant. Dimanches et jours fériés/fermetures exclus.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRecompact(false)}>Annuler</Button>
          <Button variant="warning" onClick={handleRecompact} disabled={recompacting}>
            {recompacting ? <><Spinner size="sm" animation="border" className="me-2" />Recompactage...</> : "Recompacter"}
          </Button>
        </Modal.Footer>
      </Modal>

    </Container>
  );
}
